/**
 * Aiguillage et permissions par rôle.
 *
 * ⚠ Divergence assumée avec `mobile/context/auth.tsx` : l'app native envoie
 * `admin` et `super_admin` vers l'espace staff, alors qu'aucun contrôleur métier
 * ne les accepte (`@Roles('pastor', 'secretary', …)`) — ils y prennent un 403 sur
 * chaque requête. La PWA les redirige vers un écran d'explication.
 */

/** Rôles qui disposent d'un espace staff réellement fonctionnel côté API. */
export const STAFF_ROLES = ['pastor', 'secretary', 'treasurer'] as const

/** Rôles plateforme : gestion des tenants, pas de données métier. */
export const PLATFORM_ROLES = ['admin', 'super_admin'] as const

export function isStaff(roles: string[]): boolean {
  return roles.some((r) => (STAFF_ROLES as readonly string[]).includes(r))
}

export function isPlatformRole(roles: string[]): boolean {
  return roles.some((r) => (PLATFORM_ROLES as readonly string[]).includes(r))
}

/** Route d'accueil correspondant au rôle le plus prioritaire. */
export function getHomeRoute(roles: string[]): string {
  if (isPlatformRole(roles)) return '/espace-indisponible'
  if (isStaff(roles)) return '/accueil'
  if (roles.includes('usher')) return '/scanner'
  return '/mon-qr'
}

// ─── Permissions fines ───────────────────────────────────────────────────────
// Alignées sur les décorateurs @Roles des contrôleurs back-end : une action
// masquée ici est une action qui renverrait 403.

/** `GET/POST/PATCH /members` — @Roles('pastor', 'secretary') */
export function canManageMembers(roles: string[]): boolean {
  return roles.includes('pastor') || roles.includes('secretary')
}

/** `POST /services` — @Roles('pastor', 'secretary') */
export function canCreateService(roles: string[]): boolean {
  return roles.includes('pastor') || roles.includes('secretary')
}

/** `POST /services/:id/attendances[/qr]` — @Roles('pastor', 'secretary', 'usher') */
export function canRecordAttendance(roles: string[]): boolean {
  return roles.includes('pastor') || roles.includes('secretary') || roles.includes('usher')
}

/** `GET /finance/bilan`, `GET /finance/transactions` — @Roles('pastor', 'treasurer', 'secretary') */
export function canViewFinance(roles: string[]): boolean {
  return roles.includes('pastor') || roles.includes('treasurer') || roles.includes('secretary')
}

/** `POST /finance/transactions` — @Roles('pastor', 'treasurer') */
export function canAddTransaction(roles: string[]): boolean {
  return roles.includes('pastor') || roles.includes('treasurer')
}
